/**
 * customer-environments.js — the pure logic behind the customer Environments
 * page (`/my/environments`), the "Environment" selector in the deploy wizard,
 * the Environments nav entries, the post-plan-change Dashboard nudge and the
 * Domains page's datacenter gate. No `@/` imports, so `node --test tests/unit/`
 * covers it (tests/unit/customer-environments.test.mjs).
 *
 * Product decision (owner, 2026-10-22): "Apps live in environments, every
 * organisation has one for free, more cost money." Every signed-in customer
 * sees an Environments page with their default environment; creating further
 * environments is tier-gated by `max_environments` with an upgrade hint.
 * PLATFORM environments (`/environments`, Standard #18 "platform" scope) stay
 * operator-only and are a different page with a different name.
 *
 * Backend contract (cluster-api 2.71.0, built in parallel — mocked in tests):
 *   GET /api/cluster/environments?scope=customer
 *     → rows {id, name, kind: "default"|"custom", is_default,
 *             target: {cluster_name, namespace}, status, deletable, created_at}
 *     → 403 {error: "no_environment_grant"} for a member with no grant
 *   GET /api/cluster/environments/options
 *     → {max_environments, environments_used, can_create,
 *        plan_hint: {tier_key, next_tier_key, next_plan_name}, …}
 *     → 503 {error: "environment_provisioning_disabled"} on a face with
 *       provisioning off ("creation unavailable", the list still shows)
 *   POST /api/cluster/environments
 *     → 422 {error: "max_environments_reached", plan_hint} at the cap
 *     → 409 {error: "default_environment"} when deleting the default
 *   GET /api/cluster/uts/deploy-targets rows carry `environment_id`;
 *   POST /api/cluster/uts/instances and GET /uts/quota/check accept it.
 *
 * Author: Matew Makhatadze
 */

/* ───────────────────────────── errors ───────────────────────────── */

/** HTTP status off an axios-style error (or an unwrap()-thrown Error). */
export function errorStatus(err) {
  const s = err?.response?.status ?? err?.status
  return typeof s === 'number' ? s : null
}

/** The server's error code (`{error: "…"}` or `errors[0].error`), or ''. */
export function errorCode(err) {
  const body = err?.response?.data
  if (!body || typeof body !== 'object') return ''
  if (typeof body.error === 'string') return body.error
  const first = Array.isArray(body.errors) ? body.errors[0] : null
  if (first && typeof first.error === 'string') return first.error
  if (typeof body.code === 'string') return body.code
  return ''
}

export const NO_GRANT_CODE = 'no_environment_grant'
export const PROVISIONING_OFF_CODE = 'environment_provisioning_disabled'
export const AT_CAP_CODE = 'max_environments_reached'
export const DEFAULT_ENV_CODE = 'default_environment'

/** 403 — a member with no environment grant in the active organisation. */
export function isNoGrant(err) {
  return errorStatus(err) === 403
}

/** 503 — this face has environment provisioning switched off. */
export function isProvisioningOff(err) {
  if (errorStatus(err) === 503) return true
  return errorCode(err) === PROVISIONING_OFF_CODE
}

/** 422 `max_environments_reached` — the plan's environment cap. */
export function isAtCap(err) {
  return errorStatus(err) === 422 && errorCode(err) === AT_CAP_CODE
}

/** 409 `default_environment` — the default cannot be deleted. */
export function isDefaultEnvironmentRefusal(err) {
  return errorStatus(err) === 409 && errorCode(err) === DEFAULT_ENV_CODE
}

/** The `plan_hint` block off an error body, or null. */
export function planHintOf(err) {
  const body = err?.response?.data
  const hint = body && typeof body === 'object' ? (body.plan_hint || body.data?.plan_hint) : null
  return hint && typeof hint === 'object' ? hint : null
}

export const LIST_NOTES = Object.freeze({
  forbidden:
    'You don’t have access to your organisation’s environments yet. Ask an organisation admin to grant it.',
  disabled: 'Environments are not available on this face of the platform.',
  error: 'Your environments could not be loaded. Try again.'
})

/**
 * Classify a list failure into the one quiet state the page shows. A 403 and
 * a 503 are settled answers, not failures: no Retry, no red banner, no
 * second request.
 * @returns {{kind: 'forbidden'|'disabled'|'error', note: string, retry: boolean}}
 */
export function classifyListFailure(err) {
  if (isNoGrant(err)) return { kind: 'forbidden', note: LIST_NOTES.forbidden, retry: false }
  if (isProvisioningOff(err)) return { kind: 'disabled', note: LIST_NOTES.disabled, retry: false }
  return { kind: 'error', note: err?.message || LIST_NOTES.error, retry: true }
}

/* ─────────────────────── session access memo ─────────────────────── */

/**
 * Remember, for this page session, that the customer environment endpoints
 * refused us — so no surface makes the same request again (the 2026-10-22
 * screenshot showed three red 403/503 calls per page load from
 * `environments-api.js:99/249` and `domains-api.js:91`). An org switch
 * reloads the page, which resets the memo.
 */
export function createAccessMemo() {
  const memo = { listForbidden: false, provisioningOff: false, datacentersDenied: false }
  return {
    get state() { return { ...memo } },
    /** Record a refusal from the list endpoint. Returns the classification. */
    rememberList(err) {
      const c = classifyListFailure(err)
      if (c.kind === 'forbidden') memo.listForbidden = true
      if (c.kind === 'disabled') memo.provisioningOff = true
      return c
    },
    /** Record a refusal from the options endpoint. */
    rememberOptions(err) {
      if (isProvisioningOff(err)) memo.provisioningOff = true
      if (isNoGrant(err)) memo.listForbidden = true
    },
    rememberDatacentersDenied() { memo.datacentersDenied = true },
    /** Would a list request be pointless right now? */
    skipList() { return memo.listForbidden || memo.provisioningOff },
    /** Would an options request be pointless right now? */
    skipOptions() { return memo.provisioningOff || memo.listForbidden },
    skipDatacenters() { return memo.datacentersDenied },
    reset() { memo.listForbidden = false; memo.provisioningOff = false; memo.datacentersDenied = false }
  }
}

/* ───────────────────────────── rows ───────────────────────────── */

export function environmentIdOf(env) {
  if (!env || typeof env !== 'object') return ''
  const id = env.id ?? env._id ?? env.slug ?? ''
  return id === null || id === undefined ? '' : String(id)
}

/** Where a customer environment opens. */
export function environmentPath(env) {
  const id = environmentIdOf(env)
  return id ? `/my/environments/${encodeURIComponent(id)}` : '/my/environments'
}

const STATUS_COPY = Object.freeze({
  active: 'Active',
  ready: 'Ready',
  provisioning: 'Provisioning',
  draft: 'Draft',
  suspended: 'Suspended',
  failed: 'Failed',
  deleting: 'Deleting'
})

export function statusLabel(status) {
  const s = String(status || '').trim().toLowerCase()
  if (!s) return 'Unknown'
  return STATUS_COPY[s] || (s.charAt(0).toUpperCase() + s.slice(1))
}

/** Tone for the status chip: ok / busy / warn / muted. */
export function statusTone(status) {
  const s = String(status || '').trim().toLowerCase()
  if (s === 'active' || s === 'ready') return 'ok'
  if (s === 'provisioning' || s === 'deleting') return 'busy'
  if (s === 'suspended' || s === 'failed') return 'warn'
  return 'muted'
}

/**
 * Normalise the list rows (2.71.0 shape, tolerant of the P1 shape that used
 * `target_cluster_ref` / `lifecycle` / `_id`). The default environment is
 * first; the rest keep the server order.
 */
export function normalizeCustomerEnvironments(rows) {
  const list = Array.isArray(rows) ? rows.filter((r) => r && typeof r === 'object') : []
  const out = list.map((r) => {
    const target = r.target && typeof r.target === 'object' ? r.target : {}
    const isDefault = r.is_default === true || r.kind === 'default'
    return {
      id: environmentIdOf(r),
      name: r.display || r.name || environmentIdOf(r),
      kind: r.kind || (isDefault ? 'default' : 'custom'),
      isDefault,
      cluster: target.cluster_name || target.cluster || r.target_cluster_ref || '',
      namespace: target.namespace || '',
      status: r.status || r.lifecycle || '',
      deletable: r.deletable === true,
      createdAt: r.created_at || r.createdAt || null,
      counts: r.counts && typeof r.counts === 'object' ? r.counts : null,
      instances: Array.isArray(r.instances) ? r.instances : null,
      raw: r
    }
  })
  const defaults = out.filter((e) => e.isDefault)
  const others = out.filter((e) => !e.isDefault)
  return [...defaults, ...others]
}

/** The default environment, or the first one. */
export function defaultEnvironment(envs) {
  const list = Array.isArray(envs) ? envs : []
  return list.find((e) => e && e.isDefault) || list[0] || null
}

/**
 * How many of the org's apps run in this environment — matched on the
 * instance's `environment_id` when the server stamps it, else on the target
 * cluster/namespace. Instances are the raw `GET /uts/instances` rows.
 */
export function appsInEnvironment(instances, env) {
  if (!env) return 0
  const list = Array.isArray(instances) ? instances : []
  const id = environmentIdOf(env)
  return list.filter((inst) => {
    if (!inst || typeof inst !== 'object') return false
    const stamped = inst.environment_id ?? inst.target?.environment_id
    if (stamped !== undefined && stamped !== null && stamped !== '') return String(stamped) === id
    const t = inst.target || {}
    const cluster = t.cluster_name || t.cluster || ''
    return Boolean(env.cluster) && cluster === env.cluster && (t.namespace || '') === (env.namespace || '')
  }).length
}

/**
 * The counts strip for a card: the server's `counts` when it sends them,
 * otherwise apps from the org's instance list. Domains and certificates are
 * shown only when the server counted them (never one request per card).
 * @returns {Array<{key: string, label: string, value: number}>}
 */
export function environmentCounts(env, instances) {
  const c = env && env.counts ? env.counts : {}
  let apps
  if (Number.isFinite(c.apps)) apps = c.apps
  else if (env && Array.isArray(env.instances)) apps = env.instances.length
  else apps = appsInEnvironment(instances, env)
  const out = [{ key: 'apps', label: apps === 1 ? 'app' : 'apps', value: apps }]
  if (Number.isFinite(c.domains)) out.push({ key: 'domains', label: c.domains === 1 ? 'domain' : 'domains', value: c.domains })
  if (Number.isFinite(c.certificates)) out.push({ key: 'certificates', label: c.certificates === 1 ? 'certificate' : 'certificates', value: c.certificates })
  return out
}

/* ─────────────────────────── creation ─────────────────────────── */

function tierName(key) {
  const k = String(key || '').trim()
  if (!k) return ''
  return k.charAt(0).toUpperCase() + k.slice(1).toLowerCase()
}

/**
 * "Your Starter plan includes 1 environment — upgrade to Pro for up to N."
 * Degrades honestly when the hint is partial.
 */
export function capHint({ planHint, maxEnvironments, used } = {}) {
  const h = planHint && typeof planHint === 'object' ? planHint : {}
  const plan = tierName(h.tier_key)
  const max = Number.isFinite(maxEnvironments) ? maxEnvironments : (Number.isFinite(used) ? used : null)
  let includes = 'its environments'
  if (max !== null) includes = `${max} environment${max === 1 ? '' : 's'}`
  const head = plan ? `Your ${plan} plan includes ${includes}` : `Your plan includes ${includes}`
  const next = h.next_plan_name || tierName(h.next_tier_key)
  const nextMax = Number.isFinite(h.next_max_environments) ? h.next_max_environments : null
  if (!next) return `${head}.`
  if (nextMax !== null) return `${head} — upgrade to ${next} for up to ${nextMax}.`
  return `${head} — upgrade to ${next} for more.`
}

/**
 * What the "Create environment" control does, from the options answer.
 *   can_create   → enabled
 *   at_cap       → locked, with the plan hint
 *   unavailable  → disabled with a quiet note (503 provisioning off)
 *   forbidden    → hidden (the list already says why)
 *   unknown      → disabled while options are loading / failed softly
 */
export function creationState({ options, error, loading } = {}) {
  if (loading) return { kind: 'unknown', enabled: false, note: '' }
  if (error) {
    if (isProvisioningOff(error)) {
      return { kind: 'unavailable', enabled: false, note: 'Creating environments is not available on this face of the platform.' }
    }
    if (isNoGrant(error)) return { kind: 'forbidden', enabled: false, note: '' }
    return { kind: 'unknown', enabled: false, note: 'Plan limits could not be read just now.' }
  }
  const o = options && typeof options === 'object' ? options : null
  if (!o) return { kind: 'unknown', enabled: false, note: '' }
  const max = Number.isFinite(o.max_environments) ? o.max_environments : null
  const used = Number.isFinite(o.environments_used) ? o.environments_used : null
  const canCreate = o.can_create === true || (o.can_create === undefined && max !== null && used !== null && used < max)
  if (canCreate) {
    let note = ''
    if (max !== null && used !== null) note = `${used} of ${max} environment${max === 1 ? '' : 's'} in use.`
    return { kind: 'can_create', enabled: true, note, max, used }
  }
  return {
    kind: 'at_cap',
    enabled: false,
    note: capHint({ planHint: o.plan_hint, maxEnvironments: max, used }),
    max,
    used
  }
}

/* ───────────────────────────── nav ───────────────────────────── */

/**
 * Which Environments entries the nav carries, and where.
 *   customer  → "Environments" → /my/environments, every signed-in user,
 *               in the PRIMARY row (and the drawer, which mirrors it)
 *   operator  → "Platform environments" → /environments, operators only,
 *               in the user MENU and the drawer beside "Environments console"
 * The primary row holds eight links for a signed-in customer and is full at
 * 1280 px; a ninth for operators slid "Docs" and "Contact" under the
 * Dashboard button, so the platform entry sits with the other operator
 * entries instead. The console (`/admin/environments`) stays in the menu.
 */
export function environmentsNavEntries({ isAuthenticated, isOperator } = {}) {
  const out = []
  if (!isAuthenticated) return out
  out.push({ to: '/my/environments', label: 'Environments', scope: 'customer', placement: 'primary' })
  if (isOperator === true) out.push({ to: '/environments', label: 'Platform environments', scope: 'platform', placement: 'menu' })
  return out
}

/* ───────────────────────── deploy wizard ───────────────────────── */

/** The wizard's select options from the normalised list. */
export function environmentChoices(envs) {
  const list = Array.isArray(envs) ? envs : []
  return list.filter((e) => e && e.id && e.cluster).map((e) => ({
    id: e.id,
    label: e.isDefault ? `${e.name} (default)` : e.name,
    hint: e.namespace ? `${e.cluster} / ${e.namespace}` : e.cluster,
    cluster: e.cluster,
    namespace: e.namespace || '',
    isDefault: e.isDefault
  }))
}

/** Which environment the wizard preselects: a still-valid pick, else the default. */
export function pickEnvironment(choices, keepId) {
  const list = Array.isArray(choices) ? choices : []
  const kept = keepId ? list.find((c) => c.id === String(keepId)) : null
  return kept || list.find((c) => c.isDefault) || list[0] || null
}

/** The deploy `target` an environment resolves to on the client (the server re-resolves from `environment_id`). */
export function targetFromEnvironment(choice) {
  if (!choice || !choice.cluster) return null
  return { cluster_name: choice.cluster, namespace: choice.namespace || '' }
}

/** The create-instance body with `environment_id` only when one was chosen. */
export function withEnvironmentId(payload, environmentId) {
  const body = { ...(payload || {}) }
  if (environmentId) body.environment_id = String(environmentId)
  return body
}

/* ─────────────────────── Dashboard nudge ─────────────────────── */

/**
 * After a plan purchase the user's new organisation becomes their default,
 * but the session still sits in the shared system org. Say so once.
 * @returns {{show: boolean, org: object|null}}
 */
export function orgReadyNudge({ me, dismissedIds } = {}) {
  const u = me && typeof me === 'object' ? me : null
  if (!u) return { show: false, org: null }
  const current = u.current_organization
  const target = u.default_organization
  if (!current || !target || typeof current !== 'object' || typeof target !== 'object') return { show: false, org: null }
  if (current.id === undefined || target.id === undefined) return { show: false, org: null }
  if (String(current.id) === String(target.id)) return { show: false, org: null }
  if (current.is_system_org !== true) return { show: false, org: null }
  const dismissed = Array.isArray(dismissedIds) ? dismissedIds.map(String) : []
  if (dismissed.includes(String(target.id))) return { show: false, org: target }
  return { show: true, org: target }
}

export const NUDGE_STORAGE_KEY = 'cs:org-ready-nudge-dismissed'

/** Dismissed org ids from storage; never throws (private mode, blocked storage). */
export function readDismissedNudges(storage) {
  try {
    const raw = storage && storage.getItem(NUDGE_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.map(String) : []
  } catch (_) {
    return []
  }
}

/** Remember a dismissal per org; never throws. Returns the new list. */
export function writeDismissedNudge(storage, orgId) {
  const next = Array.from(new Set([...readDismissedNudges(storage), String(orgId)]))
  try {
    if (storage) storage.setItem(NUDGE_STORAGE_KEY, JSON.stringify(next))
  } catch (_) { /* storage unavailable — the banner simply returns next load */ }
  return next
}

/* ───────────────────────── Domains gate ───────────────────────── */

/**
 * Should the Domains page ask cluster-api for the datacenter list? Not for a
 * hosted (tenant) org's member — `datacenter` read is an operator/licensed
 * grant and the wizard falls back to the datacenters seen through the org's
 * own environments. A super-admin always may; a first 403 is remembered for
 * the session by the store.
 */
export function shouldLoadDatacenters({ org, user, deniedThisSession } = {}) {
  if (deniedThisSession === true) return false
  if (user && user.is_super_admin === true) return true
  const o = org && typeof org === 'object' ? org : null
  if (!o) return false
  const archetype = String(o.archetype || 'tenant').trim().toLowerCase()
  if (archetype === 'licensed' || archetype === 'platform_owner') return true
  const planKind = String(o.plan_kind || '').trim().toLowerCase()
  if (planKind === 'license') return true
  return false
}
